/* eslint-disable max-len */
// antd
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Title from 'components/atoms/Title';
import AuthWrapper from 'components/base/AuthWrapper';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';

import { useLeiRedirect } from 'hooks/lei/useLeiRedirect';
import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import cisMenyApi from 'services/cisMeny';
import cisStatyApi from 'services/cisStaty';
import emitentApi, { IEmitent } from 'services/emitent';
import { IRozpZadostData, leiLoginlessGet } from 'services/lei/leiLoginLess';
import { IPlatbaLogin, platbaLogin } from 'services/lei/leiPlatba';
import nastavitUcetApi from 'services/nastavitUcet';
import profilyApi, { IPristupy } from 'services/profily';
import { IRedirectLoginResp, bankidRedirectLogin } from 'services/spravaEmitenta/zadostPravo';

import { EPodTypDokumentu, EPodTypdokumentuPrikaz, ETypDokumentu } from 'enums/dokumentyEnums';
import { EEmitentJe } from 'enums/isin/emitentJe';
import { EProfilyTyp } from 'enums/profilyTyp';
import routePathEnum from 'enums/routePathEnum';

import { privateIntegration } from 'data/privateIntegration';

import { _getKeyValue_ } from 'utils/getObjectKeyValue';

interface IRedirectLogin {}

const RedirectLogin: React.FC<IRedirectLogin> = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { token, cisStaty, platbaLei, lei } = useContext(Context);
    const { leiRedirect } = useLeiRedirect();
    const { setLevel } = useProfily();
    const { lang, hash } = useParams<{ lang: 'cs' | 'en'; hash: string }>();
    const [loading, setLoading] = useState<boolean>(false);
    const [leiRedirectResp, setLeiRedirectResp] = useState<IRozpZadostData>();
    const [redirectResp, setRedirectResp] = useState<IRedirectLoginResp>();
    const isDLT = window.location.hash.includes('dlt');
    const isLEI =
        window.location.hash.includes('lei') || window.location.hash.includes('platebni-brana')
            ? window.location.hash.includes('odkaz')
                ? 'loginless'
                : 'regular'
            : '';
    const redirectRespOsStates = [
        'OSSMLOUVA',
        'OSSMLOUVADLT',
        'OSDODATEK',
        'OSDODATEKDLT',
        'OSDONAVYSENI',
        'OSDONAVYSENIDLT',
        'OSNAVYSENI',
        'OSNAVYSENIDLT',
        'OSSEZUPIS',
        'OSEMISELIKV',
        'OSEMISELIST',
        'OSEMISEFUZE',
        'OSZMENJINA',
        'OSPREVODNASL',
        'OSVYPIS',
        'OSOMEZPREVOD'
    ];

    useEffect(() => {
        if (isLEI && hash && !token) {
            setLoading(true);
            if (isLEI === 'loginless') {
                leiLoginlessGet(hash).then(resp => {
                    setLeiRedirectResp(resp);
                    setLevel('token', resp.token);
                    setLevel('lei', {
                        idVarsymZadosti: {
                            id: resp.zadostoid,
                            varsym: resp.varsymbol
                        },
                        loginless: {
                            typzadosti: resp.typzadosti,
                            spolecnost: {
                                legalentityidentifier: resp.lei,
                                registrationauthorityentityid: resp.regentity,
                                jurisdikce: resp.jurisdikce,
                                regauthority: resp.regauthority,
                                typzadosti: resp.typzadosti
                            },
                            osoba: {
                                ...resp,
                                hash: hash
                            }
                        }
                    });
                });
            } else {
                platbaLogin(hash).then(resp => {
                    setLevel('language', resp.lang);
                    setTimeout(() => setLevel('platbaLei', resp), 100);
                    setLevel('token', resp.token);
                });
            }
        } else if (isDLT && hash && !token) {
            setLevel('token', hash);
            setLevel('language', lang === 'en' ? 'en' : 'cz');
        } else if (hash && !token) {
            setLoading(true);
            bankidRedirectLogin(hash).then(resp => {
                setLevel('language', resp.jazyk);
                setLevel('token', resp.token);
                setRedirectResp(resp);
                if (resp.typ === 'PRISTUP') {
                    // PRISTUP = login pomocou bankid
                    navigateWorkaround(routePathEnum.MAIN_PAGE);
                } else if (Object.keys(EPodTypDokumentu).includes(resp.typ || resp.podtyp || '')) {
                    setLevel('dokumenty', {
                        selectedZadost: {
                            iddok: resp.zadostoid,
                            podtyp: (resp.typ || resp.podtyp) as EPodTypDokumentu,
                            stavpodpisu: resp.stav,
                            typ: Object.keys(EPodTypdokumentuPrikaz).includes(resp.typ || resp.podtyp || '')
                                ? ETypDokumentu.PRIKAZY
                                : ETypDokumentu.SMLOUVY
                        },
                        goToSignatures: true,
                        dokumentyMenuReady: true
                    });
                } else if (redirectRespOsStates.includes(resp.typ || resp.podtyp || '')) {
                    setLevel('dokumenty', {
                        selectedZadost: {
                            iddok: resp.zadostoid,
                            podtyp: (resp.typ || resp.podtyp || '').split('OS')[1] as EPodTypDokumentu,
                            idosoby: resp.osobaoid,
                            stavpodpisu: resp.stav,
                            typ: Object.keys(EPodTypdokumentuPrikaz).includes(
                                (resp.typ || resp.podtyp || '').split('OS')[1] as EPodTypDokumentu
                            )
                                ? ETypDokumentu.PRIKAZY
                                : ETypDokumentu.SMLOUVY
                        },
                        dokumentyMenuReady: true
                    });
                } else {
                    resp.typ !== 'POTVRZENISLUZBY' &&
                        setLevel('zadostAgenda', {
                            [resp.typ === 'PRISTUPAMLZ' || resp.typ === 'PRISTUPAMLZBI' ? 'pristupamlz' : 'pristupc2']:
                                {
                                    id: resp.zadostoid,
                                    ...(resp.osobaoid && {
                                        idosoby: resp.osobaoid
                                    }),
                                    isBankIDredirect: true
                                }
                        });
                    resp.typ === 'C6OSOBAPDF' &&
                        setLevel('amlz', {
                            idzadosti: resp.zadostoid,
                            goToC6: true,
                            amlz: {}
                        });
                    resp.typ === 'AML' &&
                        setLevel('amlz', {
                            idzadosti: resp.zadostoid,
                            goToC2Osoba: resp.osobaoid,
                            amlz: {}
                        });
                    resp.typ === 'POTVRZENISLUZBY' &&
                        setLevel('mePozadavkyBisign', {
                            sluzba: resp.sluzba,
                            requestoid: resp.requestoid,
                            datum: resp.datum,
                            upozor: resp.upozor
                        });

                    if (cisStaty.length < 1) {
                        cisStatyApi().then(({ data }) => {
                            setLevel('cisStaty', data);
                        });
                        cisMenyApi().then(({ data }) => {
                            setLevel('cisMeny', data);
                        });
                    }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
    PRISTUPAMLZ("PRISTUPAMLZ"),
    PRISTUPAMLZBI("PRISTUPAMLZBI"), pristup

    PRISTUPC2("PRISTUPC2"),
    PRISTUPC2BI("PRISTUPC2BI"), osoba

    C2OSOBABI("C2OSOBABI"), 
    C2OSOBAPDF("C2OSOBAPDF") odkaz
    */

    const getRedirectRoute = (redResp: IRedirectLoginResp, aktivniFirma?: IPristupy, menuProfile?: IEmitent) => {
        let path: string = '';
        if (Object.keys(EPodTypDokumentu).includes(redResp.typ || redResp.podtyp || '')) {
            path = `${routePathEnum.FIRMA_PLAIN}${redResp.ucetoid}${routePathEnum.DOKUMENTY_NOVY}`;
        } else if (redirectRespOsStates.includes(redResp.typ || redResp.podtyp || '')) {
            path = routePathEnum.DOKUMENTY_SMS;
        } else if (redResp.typ === 'POTVRZENISLUZBY') {
            const idProfil: string = `profilID${aktivniFirma?.profil}${menuProfile?.adminwr ? 'A' : ''}`;
            const privateIntegrationMenu = _getKeyValue_(
                menuProfile
                    ? aktivniFirma?.typ === EProfilyTyp.ADMEMIT
                        ? menuProfile.vypis && menuProfile.upis
                            ? `profilID50${menuProfile?.adminwr ? 'A' : ''}`
                            : menuProfile.vypis
                              ? `profilID38${menuProfile?.adminwr ? 'A' : ''}`
                              : ''
                        : aktivniFirma?.typ === EProfilyTyp.EMITENT
                          ? menuProfile.vypis && menuProfile.upis
                              ? `profilID49${menuProfile?.adminwr ? 'A' : ''}`
                              : menuProfile.vypis
                                ? `profilID5${menuProfile?.adminwr ? 'A' : ''}`
                                : ''
                          : idProfil
                    : idProfil
            )(privateIntegration).DUVERNAOBLAST;
            const test = (Object.keys(privateIntegrationMenu) as [keyof typeof privateIntegrationMenu]).map(
                e => privateIntegrationMenu[e]
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const fSluzby = Object.assign({}, ...test);
            path = redResp.sluzba ? `${routePathEnum.FIRMA_PLAIN}${redResp.ucetoid}${fSluzby[redResp.sluzba].url}` : '';
        } else if (redResp.typ === 'PRISTUPAMLZ' || redResp.typ === 'PRISTUPAMLZBI') {
            path = `${routePathEnum.FIRMA_PLAIN}${redResp.ucetoid}${routePathEnum.AMLZ_ZADOST_O_AGENDU}`;
        } else if (redResp.typ === 'C6OSOBAPDF') {
            path = `${routePathEnum.FIRMA_PLAIN}${redResp.ucetoid}${routePathEnum.AMLZ_FORMULAR}`;
        } else if (redResp.typ === 'C2OSOBABI' || redResp.typ === 'C2OSOBAPDF') {
            path = `${routePathEnum.AMLZ_ZADOST_O_AGENDU}`;
        } else if (redResp.typ === 'PRISTUPC2' || redResp.typ === 'PRISTUPC2BI') {
            path = `${routePathEnum.FIRMA_PLAIN}${redResp.ucetoid}${routePathEnum.EMITENT_ZASTUPUJICI_OSOBY_ZADOST}`;
        } else if (redResp.typ === 'AML') {
            path = `${routePathEnum.FIRMA_PLAIN}${redResp.ucetoid}${routePathEnum.AMLZ_FORMULAR}`;
        } else if (redResp.typ === 'OSOBAEDIT' || redResp.typ === 'OSOBAEDITPDF') {
            path = `${routePathEnum.FIRMA_PLAIN}${redResp.ucetoid}${routePathEnum.EMITENT_ZASTUPUJICI_OSOBY_EDITACE}`;
        } else
            path = `${routePathEnum.FIRMA_PLAIN}${redResp.ucetoid}${routePathEnum.EMITENT_ZASTUPUJICI_OSOBY_EDITACE}`;

        return path;
    };

    useEffect(() => {
        if (isLEI === 'loginless' && leiRedirectResp && !leiRedirectResp.token) {
            navigateWorkaround(routePathEnum.LEI_LOGINLESS_OSOBA.replace(/:hash\?$/, hash!));
        } else if (isLEI && token && (isLEI === 'loginless' ? leiRedirectResp && lei?.loginless : platbaLei)) {
            setTimeout(
                () =>
                    leiRedirect(
                        platbaLei ? (platbaLei as IPlatbaLogin).idzadosti! : leiRedirectResp?.zadostoid!,
                        platbaLei ? (platbaLei as IPlatbaLogin).varsymbol! : leiRedirectResp?.varsymbol!
                    ),
                0
            );
        } else if (isDLT && token) {
            setTimeout(() => {
                profilyApi().then(({ data }) => {
                    setLevel('profily', data);
                    if (cisStaty.length < 1) {
                        cisStatyApi().then(resp => {
                            setLevel('cisStaty', resp.data);
                        });
                        cisMenyApi().then(resp => {
                            setLevel('cisMeny', resp.data);
                        });
                    }
                    navigateWorkaround(
                        data.sezpristupu?.length
                            ? '/detail-uctu/PODLT'
                            : routePathEnum.NOVY_UCET.replace(':typ?', 'PODLT')
                    );
                });
            }, 0);
        }
        if (hash && redirectResp?.typ !== 'PRISTUP' && token && redirectResp) {
            [...redirectRespOsStates, 'C2OSOBABI', 'C2OSOBAPDF'].includes(redirectResp.typ || '')
                ? navigateWorkaround(getRedirectRoute(redirectResp))
                : token &&
                  redirectResp &&
                  redirectResp.typ !== 'C2OSOBABI' &&
                  redirectResp.typ !== 'C2OSOBAPDF' &&
                  setTimeout(() => {
                      profilyApi(
                          undefined,
                          [
                              EPodTypdokumentuPrikaz.EMISELIKV,
                              EPodTypdokumentuPrikaz.EMISEFUZE,
                              EPodTypdokumentuPrikaz.ZMENFUZE,
                              EPodTypdokumentuPrikaz.ZMENJINA,
                              EPodTypdokumentuPrikaz.VYPIS
                          ].includes(redirectResp.typ as EPodTypdokumentuPrikaz)
                      ).then(({ data }) => {
                          setLevel('profily', data);
                          setLevel('ucty', data.sezpristupu!);
                          setLevel(
                              'aktivniFirma',
                              data.sezpristupu?.filter(e => e.iducet === redirectResp?.ucetoid)[0]!
                          );
                          redirectResp?.ucetoid &&
                              nastavitUcetApi(redirectResp.ucetoid).then(() => {
                                  const profil = data.sezpristupu?.filter(e => e.iducet === redirectResp?.ucetoid)[0]!;
                                  const profilTyp = profil.typ;
                                  if (
                                      profilTyp === EProfilyTyp.EMITENT ||
                                      profilTyp === EProfilyTyp.CDCPSRD ||
                                      profilTyp === EProfilyTyp.ADMEMIT
                                  ) {
                                      emitentApi().then(resp => {
                                          setLevel('menuProfile', {
                                              ...resp,
                                              typemitenta: resp.typemitenta || EEmitentJe.NA
                                          });
                                          navigateWorkaround(getRedirectRoute(redirectResp, profil, resp));
                                      });
                                  } else {
                                      navigateWorkaround(getRedirectRoute(redirectResp, profil));
                                  }
                              });
                      });
                  }, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, redirectResp, leiRedirectResp, platbaLei, lei]);

    return (
        <AuthWrapper>
            <LoadingWrapper loading={loading}>
                <>
                    {!hash && (
                        <>
                            <Title text="UCET_NEEXISTUJE" level={3} />
                            <ButtonComponent
                                buttontext="PRIHLASIT_SE"
                                type="primary"
                                onClick={() => navigateWorkaround(routePathEnum.LOGIN_ROZCESTNIK)}
                                id="loginRedirectBtn"
                            />
                        </>
                    )}
                </>
            </LoadingWrapper>
        </AuthWrapper>
    );
};

export default RedirectLogin;
